import React, { useState, useCallback, FC, ChangeEvent, useEffect } from 'react';
import { Input, notification, confirm } from 'src/common';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { EnterOutlined } from '@ant-design/icons';
import { IApiCompany } from 'src/models/company/types';
import { UPDATE_COMPANY_INFO } from 'src/models/company/queries';
import { useMutation } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import modalStyles from 'src/common/Modal/index.module.scss';

export const NoInfo: FC = () => {
  const { t } = useTranslation();
  return (
    <div className={styles.noInfo}>
      <FontAwesomeIcon icon={faExclamationCircle} className={styles.noInfoIcon} />
      <span>{t('AdminPage.informationNotProvided')}</span>
    </div>
  );
};

interface IProps {
  companyInfo: string;
  companyInfoContent?: string;
  companyId: string | undefined;
}

export const UserEditableInfo: FC<IProps> = props => {
  const { companyInfo, companyInfoContent, companyId } = props;

  const [editing, setEditing] = useState(false);
  const prevState = companyInfoContent || '—';
  const [inputState, setInputState] = useState<string>(companyInfoContent ? companyInfoContent : '—');
  const { t } = useTranslation();

  const [updateCompanyInfo, { error: updateCompanyError }] = useMutation<
    { updateCompanyInfo: IApiCompany },
    { id: string; [key: string]: string }
  >(UPDATE_COMPANY_INFO);

  useEffect(() => {
    setInputState(prevState);
  }, [companyInfo, companyInfoContent, prevState]);

  useEffect(() => {
    if (updateCompanyError) {
      notification.error({
        message: t('AdminPage.companyNotUpdated'),
        description: t('Common.unknownErrorDesc')
      });
    }
  }, [updateCompanyError, t]);

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setInputState(e.currentTarget.value), []);

  const onConfirm = useCallback(() => {
    if (inputState !== companyInfoContent && companyId) {
      confirm.confirm({
        title: t('AdminPage.confirmTitle'),
        okText: t('Common.yes'),
        cancelText: t('Common.no'),
        className: modalStyles.confirmModal,
        centered: true,
        onOk() {
          updateCompanyInfo({ variables: { id: companyId, [companyInfo]: inputState } }).catch(() => {
            notification.error({
              message: t('Common.unknownError'),
              description: t('Common.unknownErrorDesc')
            });
          });
        },
        onClose() {
          setInputState(prevState);
        }
      });
    } else {
      setInputState(prevState);
    }
    setEditing(!editing);
  }, [companyId, companyInfo, companyInfoContent, editing, inputState, prevState, t, updateCompanyInfo]);

  const onBlur = useCallback(() => {
    setInputState(prevState);
    setEditing(!editing);
  }, [editing, prevState]);

  return (
    <>
      {companyId ? (
        <div className={styles.cellInput}>
          {editing ? (
            <Input
              autoFocus={true}
              onPressEnter={onConfirm}
              onBlur={onBlur}
              value={inputState}
              onChange={e => onChange(e)}
              suffix={<EnterOutlined />}
              size={'small'}
            />
          ) : (
            <div className={styles.cellText} onClick={() => setEditing(!editing)}>
              <span className={styles.innerText}>{inputState}</span>
            </div>
          )}
        </div>
      ) : (
        <NoInfo />
      )}
    </>
  );
};

export default UserEditableInfo;
